import React, { useState, useEffect } from 'react';
import { BookOpen, Download, Filter, Calendar, Code, Database, Cloud, Zap, ChevronDown, FileText, Lock, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext'; // Need auth context to get token

// Constants
const notesBannerImage = "url('https://img.freepik.com/premium-photo/fiery-yellow-burnt-orange-copper-red-banner-image-website-background_1047054-2547.jpg?w=996')"; 

const NotesPage: React.FC = () => {
  const { user, token } = useAuth(); // Assuming useAuth gives token
  const [notes, setNotes] = useState<any[]>([]);
  const [filterSubject, setFilterSubject] = useState<string>('All');
  const [sortBy, setSortBy] = useState('date-desc');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNoteId, setSelectedNoteId] = useState<number | null>(null);
  const [accessKey, setAccessKey] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    // fetch('http://localhost:2025/api/notes')
    fetch('https://www.projectinfotech.in/api/notes')
        .then(res => res.json())
        .then(data => setNotes(data))
        .catch(err => console.error(err));
  }, []);

  const allSubjects = ['All', ...Array.from(new Set(notes.map(n => n.subject)))];

  const handleDownloadClick = (id: number) => {
      if (!user) {
          alert("Please login to download notes.");
          return;
      }
      setSelectedNoteId(id);
      setIsModalOpen(true);
      setErrorMsg("");
      setAccessKey("");
  };

  const handleConfirmDownload = async (e: React.FormEvent) => {
      e.preventDefault();
      setDownloading(true);
      setErrorMsg("");

      try {
        //   const res = await fetch(`http://localhost:2025/api/notes/download/${selectedNoteId}`, {
          const res = await fetch(`https://www.projectinfotech.in/api/notes/download/${selectedNoteId}`, {
              method: 'POST',
              headers: { 
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}` // Pass JWT
              },
              body: JSON.stringify({ key: accessKey })
          });

          if (res.ok) {
              // Trigger File Download
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              // Content-Disposition header se filename nikalna better hai, par abhi simple rakhte hain
              a.download = "secure_note.pdf"; 
              document.body.appendChild(a);
              a.click();
              a.remove();
              setIsModalOpen(false);
          } else {
              const msg = await res.text();
              setErrorMsg(msg || "Invalid Key");
          }
      } catch (err) {
          setErrorMsg("Connection Error");
      } finally {
          setDownloading(false);
      }
  };

  // Filter Logic
  const filteredNotes = notes.filter(note => filterSubject === 'All' || note.subject === filterSubject);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      
      {/* Banner */}
      <div className="relative bg-blue-900 py-24 shadow-xl bg-cover bg-center" style={{ backgroundImage: notesBannerImage }}>
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center text-white pt-10">
          <h1 className="text-6xl font-extrabold mb-3 tracking-wide font-aligran text-red-500">
           📚 Exclusive Study Resources 
          </h1>
          <p className="text-2xl font-light max-w-4xl mx-auto">
            Access high-quality, curated notes. Enroll to get your unique access key.
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Filters */}
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8 border-l-4 border-orange-500">
            <h2 className="text-2xl font-bold text-blue-900 mb-4 flex items-center">
                <Filter className="w-5 h-5 text-orange-500 mr-2"/> Browse Notes
            </h2>
            <div className="flex flex-wrap gap-2">
                {allSubjects.map(sub => (
                    <button key={sub} onClick={() => setFilterSubject(sub)} className={`px-3 py-1 rounded-md border ${filterSubject === sub ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
                        {sub}
                    </button>
                ))}
            </div>
        </div>
      
        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"> 
            {filteredNotes.map(note => (
                <div key={note.id} className="bg-white rounded-lg p-6 shadow-md border hover:shadow-xl transition">
                    <div className="flex items-start mb-4">
                        <div className="p-2 bg-gray-100 rounded mr-4"><FileText className="text-blue-600"/></div>
                        <div>
                            <h3 className="text-lg font-bold text-blue-900 line-clamp-1">{note.title}</h3>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{note.subject}</span>
                        </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{note.summary}</p>
                    <div className="border-t pt-3 flex justify-between items-center">
                        <span className="text-xs text-gray-500"><Calendar className="w-3 h-3 inline mr-1"/> {note.date}</span>
                        <button 
                            onClick={() => handleDownloadClick(note.id)}
                            className="text-sm bg-orange-500 text-white px-3 py-1.5 rounded hover:bg-orange-600 flex items-center"
                        >
                            <Lock className="w-3 h-3 mr-1" /> Download
                        </button>
                    </div>
                </div>
            ))}
        </div>
      </div>

      {/* --- ACCESS KEY MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-white p-6 rounded-xl shadow-2xl w-full max-w-sm animate-fade-in-up">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold flex items-center gap-2"><Lock className="text-orange-500"/> Secured Download</h3>
                    <button onClick={() => setIsModalOpen(false)}><X size={20} className="text-gray-400 hover:text-gray-600"/></button>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    This file is protected. Please enter your unique <strong>Access Key</strong> provided upon enrollment.
                </p>
                <form onSubmit={handleConfirmDownload}>
                    <input 
                        type="text" 
                        placeholder="Enter Access Key (e.g. ANMOL123)" 
                        className="w-full p-3 border border-gray-300 rounded-lg mb-3 focus:ring-2 focus:ring-orange-500 outline-none"
                        value={accessKey}
                        onChange={e => setAccessKey(e.target.value)}
                        required
                    />
                    {errorMsg && <p className="text-red-600 text-xs mb-3 font-bold">{errorMsg}</p>}
                    <button 
                        type="submit" 
                        disabled={downloading}
                        className="w-full bg-blue-900 text-white py-2 rounded-lg font-bold hover:bg-blue-800 transition disabled:bg-gray-400"
                    >
                        {downloading ? "Verifying..." : "Verify & Download"}
                    </button>
                </form>
            </div>
        </div>
      )}

    </div>
  );
};

export default NotesPage;